import { useCallback } from 'react';

import type { QueryResult } from '@apollo/client';
import type { UpdateQueryFn } from '@apollo/client/core/watchQueryOptions';
import produce from 'immer';

import type { SubscriptionDescriptor } from './ReactiveSource';
import useEntitySubscriptions from './useEntitySubscriptions';

export type UseDeleteSubscriptionsInput<EntityResponse, QueryResponse> = {
  subscription?: SubscriptionDescriptor;
  queryResult: QueryResult<QueryResponse>;
  selectEntityId: (entity: EntityResponse) => string;
  selectEntities: (resp?: QueryResponse) => EntityResponse[];
  onSubscribe: (entity: EntityResponse) => void;
};

const DEFAULT_DELETE_SUBSCRIPTION_NAME = 'delete';

export default function useDeleteSubscriptions<EntityResponse, QueryResponse>({
  subscription,
  queryResult,
  onSubscribe,
  selectEntities,
  selectEntityId,
}: UseDeleteSubscriptionsInput<EntityResponse, QueryResponse>) {
  const { subscribe: utilSubscribe, unsubscribeFrom } = useEntitySubscriptions();

  const updateQuery: UpdateQueryFn<QueryResponse> = useCallback(
    (prevState: QueryResponse, { subscriptionData }) => {
      if (!subscriptionData.data || !prevState) {
        return prevState;
      }

      const deletedEntity = subscriptionData.data[
        subscription!.name ?? DEFAULT_DELETE_SUBSCRIPTION_NAME
      ] as EntityResponse;

      if (!selectEntities(prevState).find(e => selectEntityId(e) === selectEntityId(deletedEntity))) {
        return prevState;
      }

      return produce(prevState, draftState => {
        const entities = selectEntities(draftState as QueryResponse);
        const deletedEntityIndex = entities.findIndex(e => selectEntityId(e) === selectEntityId(deletedEntity));

        entities.splice(deletedEntityIndex, 1);
      });
    },
    [subscription, selectEntities, selectEntityId]
  );

  const subscribe = useCallback(
    (entity: EntityResponse) => {
      const entityId = selectEntityId(entity);
      if (subscription) {
        utilSubscribe(
          entityId,
          queryResult.subscribeToMore({
            document: subscription.query,
            variables: {
              id: entityId,
            },
            updateQuery,
          })
        );

        onSubscribe(entity);
      }
    },
    [utilSubscribe, updateQuery, selectEntityId, queryResult, subscription, onSubscribe]
  );

  return { subscribe, unsubscribeFrom };
}
